import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPurchasesByDate, deletePendingPurchase } from '../api';
import { FiTrash2, FiFilter, FiCopy } from 'react-icons/fi';
import PurchaseFilters from '../components/PurchaseFilters';
import useCurrentUser from '../hooks/useCurrentUser';
import usePurchaseFilters from '../hooks/usePurchaseFilters';
import { hasPermission } from '../utils/permissions';
import CancelPendingSaleModal from '../components/CancelPendingSaleModal';
import { getSaleStatusMeta } from '../utils/saleStatus';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import {
  getPaymentTerm,
  getCreditDueDate,
  isPurchaseOverdue,
} from '../utils/purchaseUtils';
import { CANCEL_REASON_CATEGORY_MAP } from '../constants/cancelReasonCategories';

const PurchasesListPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [documentType, setDocumentType] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [expandedPurchases, setExpandedPurchases] = useState(() => new Set());
  const { user } = useCurrentUser();
  const { filters, setters } = usePurchaseFilters({ includeSearch: true });
  const { startDate, endDate, searchTerm } = filters;
  const navigate = useNavigate();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const pageSize = 12;
  const toneStyles = {
    gray: {
      tabActive: 'bg-gray-800 text-white border-gray-800',
      tabInactive: 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100',
      badge: 'bg-gray-100 text-gray-700',
      card: 'bg-gray-50',
      border: 'border-l-gray-300',
    },
    amber: {
      tabActive: 'bg-amber-500 text-white border-amber-500',
      tabInactive: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100',
      badge: 'bg-amber-100 text-amber-800',
      card: 'bg-amber-50',
      border: 'border-l-amber-400',
    },
    indigo: {
      tabActive: 'bg-indigo-500 text-white border-indigo-500',
      tabInactive: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
      badge: 'bg-indigo-100 text-indigo-800',
      card: 'bg-indigo-50',
      border: 'border-l-indigo-400',
    },
    emerald: {
      tabActive: 'bg-emerald-500 text-white border-emerald-500',
      tabInactive: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
      badge: 'bg-emerald-100 text-emerald-800',
      card: 'bg-emerald-50',
      border: 'border-l-emerald-400',
    },
    sky: {
      tabActive: 'bg-sky-500 text-white border-sky-500',
      tabInactive: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
      badge: 'bg-sky-100 text-sky-800',
      card: 'bg-sky-50',
      border: 'border-l-sky-400',
    },
    red: {
      tabActive: 'bg-red-500 text-white border-red-500',
      tabInactive: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
      badge: 'bg-red-100 text-red-800',
      card: 'bg-red-100',
      border: 'border-l-red-400',
    },
  };
  const resolveDocumentTone = (value) => {
    if (value === 'boleta') return 'amber';
    if (value === 'factura') return 'indigo';
    return 'indigo';
  };
  const resolvePaymentTone = (purchase) => {
    const statusNormalized = String(purchase?.saleStatus || '').toLowerCase();
    if (statusNormalized === 'cancelled' || purchase?.isDeleted) return 'red';
    const term = getPaymentTerm(purchase);
    if (term === 'credit') return 'sky';
    const status = purchase?.paymentStatus || (purchase?.isPaid ? 'paid' : 'unpaid');
    if (status === 'paid') return 'emerald';
    if (status === 'partial') return 'amber';
    return 'gray';
  };
  const resolvePaymentLabel = (purchase) => {
    const statusNormalized = String(purchase?.saleStatus || '').toLowerCase();
    if (statusNormalized === 'cancelled' || purchase?.isDeleted) return 'Eliminada';
    const term = getPaymentTerm(purchase);
    if (term === 'credit') return 'Crédito';
    const status = purchase?.paymentStatus || (purchase?.isPaid ? 'paid' : 'unpaid');
    if (status === 'paid') return 'Pagada';
    if (status === 'partial') return 'Parcial';
    return 'Pendiente';
  };
  const docLabel = (purchase) => {
    const type = (purchase?.documentType || '').toLowerCase();
    if (type === 'boleta') return 'Boleta';
    if (type === 'factura') return 'Factura';
    return 'Factura';
  };
  const formatMoney = (value) => Number(value || 0).toLocaleString('de-DE');
  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('es-CL');
  };
  const getCancelReasonText = (purchase) => {
    if (!purchase) return null;
    const category = String(purchase.cancelReasonCategory || '').trim().toLowerCase();
    const detail = String(purchase.cancelReasonDetail || '').trim();
    if (category) {
      const categoryLabel = CANCEL_REASON_CATEGORY_MAP[category]?.label || category;
      return detail ? `${categoryLabel}: ${detail}` : categoryLabel;
    }
    const legacyReason = String(purchase.cancelReason || '').trim();
    return legacyReason || null;
  };
  const togglePurchaseDetails = (purchaseId) => {
    setExpandedPurchases((prev) => {
      const next = new Set(prev);
      if (next.has(purchaseId)) {
        next.delete(purchaseId);
      } else {
        next.add(purchaseId);
      }
      return next;
    });
  };
  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      notify('Copiado al portapapeles.');
    } catch (error) {
      console.error('Error copying purchase', error);
      notify('No se pudo copiar.', { type: 'error' });
    }
  };
  const buildPurchaseCopy = (purchase) => {
    const clientName = purchase?.Client?.name || 'Venta Detalle';
    const clientRut = purchase?.Client?.rut ? `RUT: ${purchase.Client.rut}` : null;
    const status = purchase.paymentStatus || (purchase.isPaid ? 'paid' : 'unpaid');
    const statusLabel = {
      unpaid: 'Pendiente',
      partial: 'Parcial',
      paid: 'Pagada',
    };
    const term = getPaymentTerm(purchase);
    const dueDate = getCreditDueDate(purchase);
    const overdue = isPurchaseOverdue(purchase);
    const statusMeta = getSaleStatusMeta(purchase.saleStatus);
    const statusNormalized = String(purchase.saleStatus || '').toLowerCase();
    const isCancelled = statusNormalized === 'cancelled' || purchase.isDeleted;
    const paymentLabel = resolvePaymentLabel(purchase);
    const cancelReasonText = getCancelReasonText(purchase);
    const saleStatusLabel = isCancelled
      ? 'Eliminada'
      : (purchase.saleStatus === 'suspended'
        ? statusMeta.label
        : statusLabel[status] || 'Pendiente');

    return [
      `${docLabel(purchase)} #${purchase.id}`,
      `Cliente: ${clientName}`,
      clientRut,
      `Total: $${formatMoney(purchase.totalAmount)}`,
      `Pago: ${paymentLabel}`,
      `Estado: ${saleStatusLabel}`,
      `Condicion: ${term === 'credit' ? 'Crédito' : 'Contado'}`,
      purchase.balance !== undefined
        ? `Pendiente: $${formatMoney(purchase.balance || 0)}`
        : null,
      term === 'credit' && dueDate
        ? `Vence: ${dueDate.toLocaleDateString('es-CL')}${overdue ? ' (Vencido)' : ''}`
        : null,
      isCancelled ? `Motivo anulacion: ${cancelReasonText || 'Sin motivo registrado'}` : null,
      purchase.createdAt ? `Fecha: ${formatDateTime(purchase.createdAt)}` : null,
    ]
      .filter(Boolean)
      .join('\n');
  };
  useEffect(() => {
    const fetchPurchases = async () => {
      if (!branchId) return;
      setLoading(true);
      setError('');
      try {
        const trimmedSearch = searchTerm.trim();
        const params = {
          page,
          pageSize,
          includeDeleted: true,
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
        };
        const data = await getPurchasesByDate(branchId, startDate, endDate, {
          ...params,
          ...(documentType ? { documentType } : {}),
          ...(paymentFilter ? { paymentFilter } : {}),
        });
        const normalized = normalizePaginatedResponse(data);
        setPurchases(normalized.data || []);
        const normalizedTotal = normalized.total || 0;
        setTotalPurchases(normalizedTotal);
        setTotalPages(
          normalized.totalPages || Math.max(1, Math.ceil(normalizedTotal / pageSize))
        );
      } catch (err) {
        setError(err.message);
        setPurchases([]);
        setTotalPurchases(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [startDate, endDate, searchTerm, page, branchId, documentType, paymentFilter]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, searchTerm, branchId, documentType, paymentFilter]);

  const handleDelete = (purchase) => {
    if (!hasPermission(user, 'sales.delete')) return;
    if (!purchase?.id) return;
    setCancelTarget(purchase);
  };

  const handleConfirmCancel = async (cancelPayload) => {
    if (!hasPermission(user, 'sales.delete')) return;
    if (!branchId || !cancelTarget?.id) {
      throw new Error('No se pudo identificar la venta.');
    }
    try {
      await deletePendingPurchase(branchId, cancelTarget.id, cancelPayload || {});
      const nextCategory = String(cancelPayload?.cancelReasonCategory || '').trim().toLowerCase() || null;
      const nextDetail = String(cancelPayload?.cancelReasonDetail || '').trim() || null;
      const nextLegacyReason = String(cancelPayload?.cancelReason || '').trim() || null;
      setPurchases((prev) =>
        prev.map((purchase) =>
          purchase.id === cancelTarget.id
            ? {
              ...purchase,
              isDeleted: true,
              saleStatus: 'cancelled',
              cancelReasonCategory: nextCategory,
              cancelReasonDetail: nextDetail,
              cancelReason: nextLegacyReason,
            }
            : purchase
        )
      );
    } catch (error) {
      console.error('Error deleting purchase:', error.message);
      throw new Error(
        error?.response?.data?.message || 'No se pudo anular la venta pendiente.'
      );
    }
  };

  const visiblePurchases = purchases.filter(Boolean);
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Ventas</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowFilters(!showFilters)} className="bg-gray-200 text-gray-700 px-3 py-2 rounded hover:bg-gray-300 sm:hidden">
            <FiFilter />
          </button>
          {hasPermission(user, 'client.read') && (
            <Link to={`/client`} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
              Clientes
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <PurchaseFilters
          filters={filters}
          setters={setters}
          showSearch
          showDocumentType
          documentType={documentType}
          onDocumentTypeChange={setDocumentType}
          showPaymentStatus
          paymentFilter={paymentFilter}
          onPaymentFilterChange={setPaymentFilter}
          showPaymentTerm={false}
          showOverdue={false}
          showDueRange={false}
          containerClassName="bg-gray-100 mb-4"
        >
        </PurchaseFilters>
      )}

      {error && <p className="text-red-500">{error}</p>}

      {/* Purchases List */}
      <ul className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <li className="text-gray-500 text-center col-span-full">
            <InlineLoading label="Cargando ventas" />
          </li>
        ) : visiblePurchases.length === 0 ? (
          <li className="text-gray-500 text-center col-span-full">
            No hay ventas en este rango de fechas.
          </li>
        ) : (
          visiblePurchases.map((purchase) => {
            const documentTypeValue = (purchase?.documentType || '').toLowerCase();
            const docTone = resolveDocumentTone(documentTypeValue);
            const docStyles = toneStyles[docTone] || toneStyles.gray;
            const paymentTone = resolvePaymentTone(purchase);
            const paymentStyles = toneStyles[paymentTone] || toneStyles.gray;
            const paymentLabel = resolvePaymentLabel(purchase);
            const cardTone = purchase.isDeleted ? 'red' : docTone;
            const cardStyles = toneStyles[cardTone] || toneStyles.gray;
            const status = purchase.paymentStatus || (purchase.isPaid ? 'paid' : 'unpaid');
            const statusMeta = getSaleStatusMeta(purchase.saleStatus);
            const statusLabel = {
              unpaid: 'Pendiente',
              partial: 'Parcial',
              paid: 'Pagada',
            };
            const term = getPaymentTerm(purchase);
            const dueDate = getCreditDueDate(purchase);
            const overdue = isPurchaseOverdue(purchase);
            const statusNormalized = (purchase.saleStatus || '').toLowerCase();
            const isOpen = statusNormalized === 'open';
            const isSuspended = statusNormalized === 'suspended';
            const isCompleted = statusNormalized === 'completed' || status === 'paid' || purchase.isPaid;
            const isCancelled = statusNormalized === 'cancelled' || purchase.isDeleted;
            const cancelReasonText = getCancelReasonText(purchase);
            const needsPayment =
              statusNormalized === 'completed'
              && !purchase.isDeleted
              && purchase.paymentTerm !== 'credit'
              && !purchase.paymentMethod
              && !purchase.isPaid;
            const clientName = purchase?.Client?.name || 'Venta Detalle';
            const clientRut = purchase?.Client?.rut;
            const isExpanded = expandedPurchases.has(purchase.id);
            const detailsId = `purchase-${purchase.id}-details`;
            const saleStatusLabel = isCancelled
              ? 'Eliminada'
              : (purchase.saleStatus === 'suspended'
                ? statusMeta.label
                : statusLabel[status] || 'Pendiente');
            const saleStatusDetailLabel = isCancelled ? 'Eliminada' : statusMeta.label;
            const canDelete = status === 'unpaid' && isOpen;
            return (
              <li
                key={purchase.id}
                className={`border border-l-4 ${cardStyles.border} ${cardStyles.card} p-4 rounded-lg shadow`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">#{purchase.id}</p>
                    <h3 className="text-lg font-semibold">{clientName}</h3>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex flex-wrap items-center gap-1 text-[11px] font-semibold uppercase tracking-wide">
                      <span className={`px-2 py-0.5 rounded-full ${docStyles.badge}`}>
                        {docLabel(purchase)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full ${paymentStyles.badge}`}>
                        {paymentLabel}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => togglePurchaseDetails(purchase.id)}
                      className="text-xs bg-white/70 text-gray-600 px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                      aria-expanded={isExpanded}
                      aria-controls={detailsId}
                    >
                      {isExpanded ? 'Ocultar' : 'Ver mas'}
                    </button>
                  </div>
                </div>
                <div className="space-y-1 text-sm text-gray-700">
                  <p>Total: ${formatMoney(purchase.totalAmount)}</p>
                  <p className="text-xs text-gray-600">Estado: {saleStatusLabel}</p>
                  {isCancelled && (
                    <p className="text-xs text-red-700">
                      Motivo anulacion: {cancelReasonText || 'Sin motivo registrado'}
                    </p>
                  )}
                  <p className="text-xs text-gray-600">Fecha: {formatDateTime(purchase.createdAt)}</p>
                </div>
                {isExpanded && (
                  <div id={detailsId} className="mt-2 space-y-1 text-xs text-gray-600">
                    <p>RUT: {clientRut || '—'}</p>
                    <p>Condición: {term === 'credit' ? 'Crédito' : 'Contado'}</p>
                    {purchase.balance !== undefined && (
                      <p>Pendiente: ${formatMoney(purchase.balance || 0)}</p>
                    )}
                    {term === 'credit' && dueDate && (
                      <p className={overdue ? 'text-red-600' : 'text-gray-600'}>
                        Vence: {dueDate.toLocaleDateString('es-CL')}
                        {overdue ? ' (Vencido)' : ''}
                      </p>
                    )}
                    <p>Estado venta: {saleStatusDetailLabel}</p>
                    <p>Estado pago: {paymentLabel}</p>
                    {isCancelled && (
                      <p className="text-red-700">
                        Motivo anulacion: {cancelReasonText || 'Sin motivo registrado'}
                      </p>
                    )}
                  </div>
                )}
                <div className="flex justify-between gap-2 mt-3">
                  {canDelete && hasPermission(user, 'sales.delete') && !isCancelled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(purchase);
                      }}
                      className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(buildPurchaseCopy(purchase))}
                      className="bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300 flex items-center gap-1"
                      type="button"
                    >
                      <FiCopy size={14} />
                      Copiar
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/branches/${branchId}/purchases/${purchase.id}`);
                      }}
                      className="bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600"
                    >
                      Ver
                    </button>
                    {(isOpen || isSuspended) && !isCancelled && hasPermission(user, 'sales.update') && (
                      <button
                        onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}/edit`)}
                        className="bg-yellow-500 text-white px-3 py-2 rounded hover:bg-yellow-600"
                      >
                        Continuar compra
                      </button>
                    )}
                    {needsPayment && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/branches/${branchId}/purchases/${purchase.id}/receipt`);
                        }}
                        className="bg-emerald-500 text-white px-3 py-2 rounded hover:bg-emerald-600"
                      >
                        Registrar pago
                      </button>
                    )}
                    {isCompleted && !needsPayment && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/branches/${branchId}/purchases/${purchase.id}/receipt`);
                        }}
                        className="bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600"
                      >
                        Comprobante
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })
        )}
      </ul>

      {totalPurchases > pageSize && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-6">
          <div>
            Mostrando {totalPurchases === 0 ? 0 : (currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalPurchases)} de {totalPurchases}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Anterior
            </button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
      <CancelPendingSaleModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        saleLabel={cancelTarget ? `${docLabel(cancelTarget)} #${cancelTarget.id}` : ''}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};

export default PurchasesListPage;
